import { useState } from "react"
import { useGetSettingsQuery, useCreateSettingMutation, useBulkUpdateSettingsMutation } from "../features/admin/adminApi"
import { Settings, Globe, Mail, CreditCard, Shield, Zap, Wrench, Save, Plus } from "lucide-react"
import type { PlatformSetting } from "../features/admin/adminApi"

type TabKey = "site" | "email" | "payment" | "security" | "features" | "maintenance"

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>("site")
  const [editedSettings, setEditedSettings] = useState<Record<string, string>>({})
  const [showAddModal, setShowAddModal] = useState(false)
  const [newSetting, setNewSetting] = useState({ key: "", value: "", description: "" })

  const { data: settingsData, isLoading } = useGetSettingsQuery({})
  const [createSetting] = useCreateSettingMutation()
  const [bulkUpdate, { isLoading: isSaving }] = useBulkUpdateSettingsMutation()

  const tabs = [
    { key: "site" as TabKey, label: "Site Configuration", icon: Globe, color: "blue" },
    { key: "email" as TabKey, label: "Email Settings", icon: Mail, color: "green" },
    { key: "payment" as TabKey, label: "Payment Gateway", icon: CreditCard, color: "purple" },
    { key: "security" as TabKey, label: "Security", icon: Shield, color: "red" },
    { key: "features" as TabKey, label: "Feature Flags", icon: Zap, color: "yellow" },
    { key: "maintenance" as TabKey, label: "Maintenance", icon: Wrench, color: "orange" },
  ]

  const currentSettings = settingsData?.[activeTab] || []

  const handleInputChange = (key: string, value: string) => {
    setEditedSettings((prev) => ({ ...prev, [key]: value }))
  }

  const handleSaveChanges = async () => {
    const changes = Object.entries(editedSettings).map(([key, value]) => ({ key, value }))
    if (changes.length === 0) {
      alert("No changes to save")
      return
    }

    try {
      await bulkUpdate({ settings: changes }).unwrap()
      setEditedSettings({})
      alert("Settings updated successfully")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to update settings")
    }
  }

  const handleCreateSetting = async () => {
    if (!newSetting.key || !newSetting.value) {
      alert("Key and value are required")
      return
    }

    try {
      await createSetting({
        key: newSetting.key,
        value: newSetting.value,
        category: activeTab,
        description: newSetting.description || undefined,
      }).unwrap()
      setShowAddModal(false)
      setNewSetting({ key: "", value: "", description: "" })
      alert("Setting created successfully")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to create setting")
    }
  }

  const getValue = (setting: PlatformSetting) => {
    return editedSettings[setting.key] !== undefined ? editedSettings[setting.key] : setting.value
  }

  const hasChanges = Object.keys(editedSettings).length > 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Settings</h1>
          <p className="text-sm text-gray-600 mt-1">Configure platform-wide settings and features</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Plus size={18} />
            Add Setting
          </button>
          {hasChanges && (
            <button
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save size={18} />
              {isSaving ? "Saving..." : `Save Changes (${Object.keys(editedSettings).length})`}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Settings Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading settings...</div>
        ) : currentSettings.length === 0 ? (
          <div className="p-8 text-center">
            <Settings className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-600">No settings configured for this category yet</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add First Setting
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {currentSettings.map((setting) => (
              <div key={setting.id} className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="block text-sm font-medium text-gray-900">{setting.key}</label>
                      {setting.isEncrypted && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded">
                          Encrypted
                        </span>
                      )}
                    </div>
                    {setting.description && (
                      <p className="text-sm text-gray-600 mb-3">{setting.description}</p>
                    )}
                    {renderSettingInput(setting, getValue(setting), (value) =>
                      handleInputChange(setting.key, value)
                    )}
                    {setting.updatedAt && (
                      <p className="text-xs text-gray-500 mt-2">
                        Last updated: {new Date(setting.updatedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Setting Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add New Setting</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Setting Key *</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newSetting.key}
                  onChange={(e) => setNewSetting((prev) => ({ ...prev, key: e.target.value }))}
                  placeholder="e.g., site.name, email.smtp_host"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Value *</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newSetting.value}
                  onChange={(e) => setNewSetting((prev) => ({ ...prev, value: e.target.value }))}
                  placeholder="Setting value"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={newSetting.description}
                  onChange={(e) => setNewSetting((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Description of this setting"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Category:</span> {activeTab}
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setNewSetting({ key: "", value: "", description: "" })
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSetting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Setting
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function renderSettingInput(
  setting: PlatformSetting,
  value: string,
  onChange: (value: string) => void
) {
  // Infer input type based on setting key or value
  const key = setting.key.toLowerCase()

  // Boolean toggle
  if (
    value === "true" ||
    value === "false" ||
    key.includes("enable") ||
    key.includes("is_") ||
    key.includes("has_") ||
    key.includes("allow")
  ) {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={value === "true"}
          onChange={(e) => onChange(e.target.checked ? "true" : "false")}
          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">{value === "true" ? "Enabled" : "Disabled"}</span>
      </label>
    )
  }

  // Number input
  if (key.includes("port") || key.includes("timeout") || key.includes("limit") || key.includes("max")) {
    return (
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    )
  }

  // Textarea for long text
  if (key.includes("message") || key.includes("description") || key.includes("template")) {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
      />
    )
  }

  // Password input for sensitive data
  if (
    setting.isEncrypted ||
    key.includes("password") ||
    key.includes("secret") ||
    key.includes("key") ||
    key.includes("token")
  ) {
    return (
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
      />
    )
  }

  // Default: text input
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  )
}
